/**
 * SearchHighlight — Tiptap extension for Find/Replace inline highlighting.
 *
 * Mirrors the CodexHighlight pattern: a ProseMirror plugin with DecorationSet
 * updated via a command + transaction meta. Decorations are non-destructive —
 * they never move the editor selection or steal focus.
 *
 * Usage:
 *   editor.commands.setSearchMatches(matches, currentIndex)
 *   editor.commands.setSearchMatches([], -1)   // clear
 */

import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import type { EditorState, EditorStateConfig, Transaction } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import type { Node as PmNode } from '@tiptap/pm/model';

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export interface SearchMatch {
  from: number;
  to: number;
}

// ---------------------------------------------------------------------------
// Plugin state
// ---------------------------------------------------------------------------

interface SearchPluginState {
  matches: SearchMatch[];
  current: number;
  decorations: DecorationSet;
}

const SEARCH_KEY = new PluginKey<SearchPluginState>('searchHighlight');

/** Meta shape dispatched by the setSearchMatches command */
interface SearchMeta {
  matches: SearchMatch[];
  current: number;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Build inline decorations for all matches. The active match receives an
 * additional class so CSS can style it differently.
 */
function buildDecorations(
  doc: PmNode,
  matches: SearchMatch[],
  current: number,
): DecorationSet {
  if (matches.length === 0) return DecorationSet.empty;

  const decorations = matches.map((m, i) => {
    const cls = i === current ? 'search-match search-match-current' : 'search-match';
    return Decoration.inline(m.from, m.to, { class: cls });
  });

  return DecorationSet.create(doc, decorations);
}

// ---------------------------------------------------------------------------
// Command type augmentation
// ---------------------------------------------------------------------------

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    searchHighlight: {
      /**
       * Replace the search match list and recompute decorations.
       * Pass an empty array + -1 to clear all highlights.
       */
      setSearchMatches: (matches: SearchMatch[], current: number) => ReturnType;
    };
  }
}

// ---------------------------------------------------------------------------
// Extension
// ---------------------------------------------------------------------------

export const SearchHighlight = Extension.create({
  name: 'searchHighlight',

  addCommands() {
    return {
      setSearchMatches:
        (matches: SearchMatch[], current: number) =>
        ({ tr, dispatch }) => {
          if (dispatch) {
            const meta: SearchMeta = { matches, current };
            dispatch(tr.setMeta(SEARCH_KEY, meta));
          }
          return true;
        },
    };
  },

  addProseMirrorPlugins() {
    return [
      new Plugin<SearchPluginState>({
        key: SEARCH_KEY,

        state: {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          init(_config: EditorStateConfig, _editorState: EditorState): SearchPluginState {
            return { matches: [], current: -1, decorations: DecorationSet.empty };
          },

          apply(
            tr: Transaction,
            old: SearchPluginState,
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            _prevState: EditorState,
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            _nextState: EditorState,
          ): SearchPluginState {
            // Explicit update via command meta
            const meta = tr.getMeta(SEARCH_KEY) as SearchMeta | undefined;
            if (meta !== undefined) {
              return {
                matches: meta.matches,
                current: meta.current,
                decorations: buildDecorations(tr.doc, meta.matches, meta.current),
              };
            }

            // No doc change → no work needed
            if (!tr.docChanged) return old;

            // Doc changed with no active matches → stay empty
            if (old.matches.length === 0) return old;

            // Doc changed: remap positions so decorations stay valid.
            // We rely on DecorationSet.map to handle this cheaply.
            return {
              ...old,
              decorations: old.decorations.map(tr.mapping, tr.doc),
            };
          },
        },

        props: {
          decorations(state: EditorState): DecorationSet | null | undefined {
            return SEARCH_KEY.getState(state)?.decorations;
          },
        },
      }),
    ];
  },
});
